import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { DocumentChartBarIcon, XMarkIcon, EyeIcon } from "@heroicons/react/24/solid";
import { t } from "i18next";
/***/

/* Components */
import Loading from "../loading/Loading";
import Switch from "../switch/Switch";
import CustomerDisplay from "../data/customer/CustomerDisplay";
import CustomerEditButton from "./CustomerEditButton";
/***/

/* Services */
import { api } from "../../services/api/api";
import {
  downloadWorkorderReport,
  previewWorkorderReport,
} from "../../services/export/workorderReport";
/***/

/**
* ExportPdfModal: display PDF export modal with image toggle and customer languages
* @param workorderId - workorder ID
* @param customer - customer object
* @param open - modal open state
* @param onClose - close modal callback
*/
export default function ExportPdfModal({
  workorderId,
  workorderRef,
  customer,
  open,
  onClose
}: {
  workorderId: string;
  workorderRef: string;
  customer?: EasyTech.Customer;
  open: boolean;
  onClose: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [includeImages, setIncludeImages] = useState<boolean>(true);
  const [compactLayout, setCompactLayout] = useState<boolean>(() => {
    const currentRealm = localStorage.getItem("realm");
    return currentRealm !== "ro";
  });
  const [customerLanguages, setCustomerLanguages] = useState<any[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
  const [customerData, setCustomerData] = useState<EasyTech.Customer | undefined>(customer);
  // Only set from the detailed endpoint. The `customer` prop is a simple response and must never
  // reach the edit form, so this stays null until (and unless) the detail is actually loaded.
  const [customerDetail, setCustomerDetail] = useState<EasyTech.Customer | null>(null);

  /**
   * Récupère le détail du customer (langues incluses).
   *
   * The `customer` prop is a CustomerSimpleResponse taken off the workorder: no reportTemplate, no
   * sendInterventionReportEmail, no languages. Reading the detailed endpoint gives the language list
   * this modal needs *and* the settings the edit form below has to be seeded with, in one
   * unrestricted call instead of the previous admin-only languages one.
   */
  /** Single place where a detailed customer becomes this modal's state, whether read or written. */
  const applyCustomerDetail = (detail: EasyTech.Customer) => {
    setCustomerData(detail);
    setCustomerDetail(detail);
    setCustomerLanguages(Array.isArray(detail?.languages) ? detail.languages : []);
  };

  const fetchCustomerDetail = async (customerId: string) => {
    try {
      setIsLoadingLanguages(true);
      applyCustomerDetail(await api.get(`users/customers/${customerId}`) as EasyTech.Customer);
    } catch (error) {
      console.error("Erreur lors de la récupération du customer:", error);
      setCustomerDetail(null);
      setCustomerLanguages([]);
    } finally {
      setIsLoadingLanguages(false);
    }
  };

  // Récupérer le détail du customer quand le modal s'ouvre
  useEffect(() => {
    if (open && customer?.id) {
      fetchCustomerDetail(customer.id);
    }
  }, [open, customer?.id]);

  // Repartir du customer fourni quand on passe à un AUTRE client. Keyed on the id, not on the
  // object identity: a parent re-render passing an equivalent inline object must not blank the
  // detail already loaded (which would disable the edit button mid-session).
  useEffect(() => {
    setCustomerData(customer);
    setCustomerDetail(null);
  }, [customer?.id]);

  const handleCustomerUpdate = async (updatedCustomer: EasyTech.Customer) => {
    try {
      // The update answers with the detailed customer, so it refreshes the modal on its own -- no
      // re-read, and no trusting the form's own copy either (it never holds contacts or machines).
      const saved = await api.put(`admin/customers/${updatedCustomer.id}`, updatedCustomer) as EasyTech.Customer;
      applyCustomerDetail(saved);

      toast.success(t("client:updateSuccess"));
    } catch (error) {
      console.error("Erreur lors de la mise à jour du customer:", error);
      toast.error(t("client:updateError"));
      throw error;
    }
  };

  const handleEditCustomer = () => {
    // Fermer le modal d'export
    onClose();
  };

  const handleDownloadPdf = async (languageIsoCode?: string) => {
    try {
      setIsDownloading(true);

      await downloadWorkorderReport({
        workorderId,
        workorderRef,
        languageIsoCode,
        includeImages,
        compactLayout,
      });
      
      toast.success(t("workorder:report:downloadSuccess"));
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF:", error);
      toast.error(t("workorder:report:downloadError"));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewPdf = async (languageIsoCode?: string) => {
    try {
      await previewWorkorderReport({
        workorderId,
        workorderRef,
        languageIsoCode,
        includeImages,
        compactLayout,
      });
      
      toast.success(t("workorder:report:viewSuccess"));
    } catch (error) {
      console.error("Erreur lors de la visualisation du PDF:", error);
      toast.error(t("workorder:report:viewError"));
    }
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal">
      <Card className="te-modal-content rounded-lg">
        <button className="close-button" onClick={() => onClose()} disabled={isDownloading}>
          <XMarkIcon className="w-4"/>
        </button>
        <CardBody>
          <div className="flex items-center gap-3 mb-5">
            <div className="rounded-full border p-2 bg-blue-200">
              <DocumentChartBarIcon className="w-7 text-blue-700" /> 
            </div>
            <Typography variant="h5">
              {t("workorder:report:downloadDescription")}
            </Typography>
          </div>
         
          <div className="field flex flex-col gap-4">
            <Typography>
              {isDownloading 
                ? t("workorder:report:downloadInProgress") 
                : t("workorder:report:exportPdf")
              }
            </Typography>

            {!isDownloading && (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex flex-col">
                    <Typography variant="h6" className="text-sm font-medium">
                      {t("workorder:report:includeImages")}
                    </Typography>
                    <Typography className="text-xs text-gray-600">
                      {t("workorder:report:includeImagesDescription")}
                    </Typography>
                  </div>
                  <Switch 
                    checked={includeImages}
                    onChange={setIncludeImages}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex flex-col">
                    <Typography variant="h6" className="text-sm font-medium">
                      {t("workorder:report:compactLayout")}
                    </Typography>
                    <Typography className="text-xs text-gray-600">
                      {t("workorder:report:compactLayoutDescription")}
                    </Typography>
                  </div>
                  <Switch 
                    checked={compactLayout}
                    onChange={setCompactLayout}
                  />
                </div>
              </>
            )}

            {/* Langues du customer */}
            {isLoadingLanguages ? (
              <div className="flex items-center justify-center p-4">
                <Loading />
                <Typography className="ml-2 text-sm text-gray-600">
                  {t("workorder:report:loadingLanguages")}
                </Typography>
              </div>
            ) : customerLanguages && customerLanguages.length > 0 && !isDownloading ? (
              <div className="flex flex-col gap-3">
                <Typography variant="h6" className="text-sm font-medium">
                  {t("workorder:report:languageOptions")}
                </Typography>
                <div className="max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-1 gap-2">
                    {customerLanguages.map((languageItem: any) => (
                      <div key={languageItem.id} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div className="flex flex-col">
                          <Typography variant="h6" className="text-sm font-medium">
                            {languageItem.language.label}
                          </Typography>
                          <Typography className="text-xs text-gray-600">
                            {t("workorder:report:languageDescription", { language: languageItem.language.label })}
                          </Typography>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            variant="outlined"
                            color="blue"
                            className="flex items-center gap-1"
                            onClick={() => handleViewPdf(languageItem.language.isoCode)}
                          >
                            <EyeIcon className="w-4 h-4" />
                            {t("workorder:report:preview")}
                          </Button>
                          <Button 
                            size="sm"
                            color="blue"
                            className="flex items-center gap-1"
                            onClick={() => handleDownloadPdf(languageItem.language.isoCode)}
                          >
                            <DocumentChartBarIcon className="w-4 h-4" />
                            {t("workorder:report:download")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (!customerLanguages || customerLanguages.length === 0) && !isLoadingLanguages && !isDownloading && customerData ? (
              <div className="flex flex-col gap-4">
                <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                  <Typography variant="h6" className="text-sm font-medium text-orange-800 mb-2">
                    {t("workorder:report:noLanguagesTitle")}
                  </Typography>
                  <Typography className="text-xs text-orange-700 mb-4">
                    {t("workorder:report:noLanguagesDescription")}
                  </Typography>
                  
                                     <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                     <CustomerDisplay customer={customerData} size="small" />
                     <CustomerEditButton
                       customer={customerDetail}
                       onEditStart={handleEditCustomer}
                       onCustomerUpdate={handleCustomerUpdate}
                     />
                   </div>
                </div>
              </div>
            ) : null}
          </div>
        </CardBody>
        {isDownloading && (
          <CardFooter className="pt-0 flex gap-2 justify-center">
            <Loading />
          </CardFooter>
        )}
      </Card>
    </Modal>
  );
}
/***/ 